/* eslint-disable max-lines */
import { showErrorAlert } from 'actions';
import DateRangePicker from 'components/Calendar';
import SelectRedesign from 'components/Select/SelectRedesign';
import moment from 'moment-timezone'; // require
import React, { useEffect, useRef, useState } from 'react';
import { DateRange, TZDate } from 'react-day-picker';
import { useDispatch } from 'react-redux';
import { getPrimaryTimezone } from 'selectors/auth';
import { useSelector } from 'store';
import logEvent from 'utils/analytics';
import { isValidDate } from 'utils/dateValidation';
import { dateFormat, dateFormatHalfYear, dateFormatYear } from 'utils/timeRanges';
import { dateOptions } from './DashboardDateFilter';

type DateType = {
  createdAfter: string;
  createdBefore: string;
};

type ComponentProps = {
  className?: string;
  showRangeOptions?: boolean;
  dateRange: DateType;
  handleSetDate: (dateRange: DateType) => void;
  handleRangeOptionChange?: (
    prevOption: string,
    selectedOption: string,
    dateRange: DateType
  ) => void;
};

type DateOption = 'date-range' | 'before' | 'after';

export const timeRangeOptions = [
  { label: 'Past 24 Hours', value: dateFormat(24, 'hours') },
  { label: 'Past 7 Days', value: dateFormat(7, 'days') },
  { label: 'Past 30 Days', value: dateFormat(30, 'days') },
  { label: 'Past 60 Days', value: dateFormat(60, 'days') },
  { label: 'Past 90 Days', value: dateFormat(90, 'days') },
  { label: 'Past 6 Months', value: dateFormatHalfYear() },
  { label: 'Year to Date', value: dateFormatYear() },
];

const fullDateOptions = [...dateOptions, ...timeRangeOptions];

const DateFilterGeneral: React.FC<ComponentProps> = (props) => {
  const {
    className,
    showRangeOptions = true,
    dateRange,
    handleSetDate,
    handleRangeOptionChange,
  } = props;

  const ref = useRef<HTMLDivElement>(null);

  const dispatch = useDispatch();

  const timeZone = useSelector(getPrimaryTimezone);
  moment.tz.setDefault(timeZone);

  const [showCalendar, setShowCalendar] = useState(false);
  const [dateOption, setDateOption] = useState<DateOption>('date-range');

  const { createdAfter, createdBefore } = dateRange;

  let fromValue = '';
  let toValue = '';

  const createdAfterDate = isValidDate(createdAfter) ? moment(createdAfter as string) : undefined;
  if (createdAfterDate) {
    fromValue = createdAfterDate.format('MM/DD/yyyy');
  }

  const createdBeforeDate = isValidDate(createdBefore)
    ? moment(createdBefore as string)
    : undefined;
  if (createdBeforeDate) {
    toValue = createdBeforeDate.format('MM/DD/yyyy');
  }

  const [fromInput, setFromInput] = useState(fromValue);
  const [toInput, setToInput] = useState(toValue);

  useEffect(() => {
    if (fromValue && !toValue) {
      setDateOption('after');
    }
    if (!fromValue && toValue) {
      setDateOption('before');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (fromValue) {
      setFromInput(fromValue);
    }
    if (toValue) {
      setToInput(toValue);
    }
  }, [fromValue, toValue]);

  useEffect(() => {
    if (!createdAfter && !createdBefore) {
      setDateOption('date-range');
    }
  }, [createdAfter, createdBefore]);

  const createdAfterTZDate = isValidDate(createdAfter)
    ? new TZDate(createdAfter as string, timeZone)
    : undefined;
  const createdBeforeTZDate = isValidDate(createdBefore)
    ? new TZDate(createdBefore as string, timeZone)
    : undefined;

  const handleSetDateRange = (range: DateRange): void => {
    logEvent('envelopes-list-filter-date');

    const { from, to } = range;

    const fromDate = from ? moment(from) : null;
    const toDate = to ? moment(to) : null;

    const fromUrl = fromDate && fromDate.isValid() ? fromDate.startOf('day').utc().format() : null;
    const toUrl = toDate && toDate.isValid() ? toDate.endOf('day').utc().format() : null;

    if (fromUrl && !toUrl) {
      handleSetDate({ createdAfter: fromUrl, createdBefore: fromUrl });
    } else {
      handleSetDate({ createdAfter: fromUrl || '', createdBefore: toUrl || '' });
    }
  };

  const handleChangeDate = (mode: string, value: Date | undefined): void => {
    if (mode === 'after') {
      handleSetDateRange({ from: value, to: moment(toValue, 'MM/DD/YYYY', true).toDate() });
    } else {
      handleSetDateRange({ from: moment(fromValue, 'MM/DD/YYYY', true).toDate(), to: value });
    }
  };

  const handleFromChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { value } = e.target;
    setFromInput(value);

    const fromDate = moment(value, 'MM/DD/YYYY', true);

    if (createdBeforeDate && moment(createdBeforeDate).isValid()) {
      const beforeDate = moment(createdBeforeDate);
      if (fromDate.isAfter(beforeDate)) {
        dispatch(showErrorAlert('Invalid Date Range'));
        return;
      }
    }

    if (fromDate.isValid()) {
      handleChangeDate('after', fromDate.toDate());
    } else if (value.length > 9) {
      dispatch(showErrorAlert('Invalid Date'));
    } else if (value.length === 0) {
      handleChangeDate('after', undefined);
    }
  };

  const handleToChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { value } = e.target;
    setToInput(value);

    const toDate = moment(value, 'MM/DD/YYYY', true);

    if (createdAfterDate && moment(createdAfterDate).isValid()) {
      const afterDate = moment(createdAfterDate);
      if (toDate.isBefore(afterDate)) {
        dispatch(showErrorAlert('Invalid Date Range'));
        return;
      }
    }

    if (toDate.isValid()) {
      handleChangeDate('before', toDate.toDate());
    } else if (value.length > 9) {
      dispatch(showErrorAlert('Invalid Date'));
    } else if (value.length === 0) {
      handleChangeDate('before', undefined);
    }
  };

  const defaultSelected: DateRange = {
    from: createdAfterTZDate,
    to: createdBeforeTZDate,
  };

  const handleDateOptionChanged = (value: DateOption): void => {
    const prevOption = dateOption;
    setDateOption(value);

    if (handleRangeOptionChange) {
      handleRangeOptionChange(prevOption, value, dateRange);
    }
  };

  return (
    <div
      aria-hidden
      ref={ref}
      className={`flex flex-col gap-2 mt-2 shadow-sm ${className || ''}`}
      onClick={(): void => setShowCalendar(true)}
    >
      {showRangeOptions && (
        <div className="bg-litlingo-success-light bg-opacity-10 rounded ">
          <SelectRedesign
            className="bg-transparent border-none"
            valueClassName="text-white"
            optionClassname="text-litlingo-gray-6"
            onChange={(value): void => {
              handleDateOptionChanged(value as DateOption);
            }}
            options={fullDateOptions}
            value={dateOption}
          />
        </div>
      )}
      {dateOption === 'after' || dateOption === 'before' || dateOption === 'date-range' ? (
        <div className="w-full h-8 flex flex-row justify-start gap-1 px-1 py-1 text-body bg-white border border-litlingo-gray-2 rounded">
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M5.5 2.75C5.5 2.33579 5.16421 2 4.75 2C4.33579 2 4 2.33579 4 2.75V4H3C1.89543 4 1 4.89543 1 6V17C1 18.1046 1.89543 19 3 19H17C18.1046 19 19 18.1046 19 17V6C19 4.89543 18.1046 4 17 4H16.5V2.75C16.5 2.33579 16.1642 2 15.75 2C15.3358 2 15 2.33579 15 2.75V4H5.5V2.75ZM17.5 8H2.5V17C2.5 17.2761 2.72386 17.5 3 17.5H17C17.2761 17.5 17.5 17.2761 17.5 17V8Z"
              fill="#5C5C5C"
            />
          </svg>

          <div className="w-full flex text-body flex-row gap-1">
            {dateOption !== 'before' && (
              <input
                data-testid="date-filter-input"
                type="text"
                placeholder={moment().format('MM/DD/yyyy')}
                className="bg-transparent placeholder-litlingo-gray-3 w-19"
                value={fromInput}
                onChange={handleFromChange}
              />
            )}
            {dateOption !== 'before' && dateOption !== 'after' && <span>-</span>}
            {dateOption !== 'after' && (
              <input
                data-testid="date-filter-input"
                type="text"
                placeholder={moment().format('MM/DD/yyyy')}
                className="bg-transparent placeholder-litlingo-gray-3 w-19"
                value={toInput}
                onChange={handleToChange}
              />
            )}
          </div>
        </div>
      ) : null}

      {showCalendar && (
        <DateRangePicker
          containerRef={ref}
          defaultRange={defaultSelected}
          setShowCalendar={(): void => setShowCalendar(!showCalendar)}
          setDateRange={handleSetDateRange}
          dateOption={dateOption}
          timeZone={timeZone}
        />
      )}
    </div>
  );
};
export default DateFilterGeneral;
